import type { MouseEvent } from 'react'
import { type FC, useCallback, useMemo, useState } from 'react'

import { VaultMediaType } from '@api'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { type Media } from '@/modules/chatting/types'
import { VaultMediaPreview } from '@/modules/vault/ui/media/vault-media-preview'

import { type DialogProps } from '../types'
import { MediaTypeFilter } from './MediaTypeFilter'
import { getMediaTypes } from './utils'

interface Props extends DialogProps {
  allMedias: Media[]
}

export const PreviewMediaDialog: FC<Props> = ({
  isOpen,
  onClose,
  allMedias,
}) => {
  const [selectedMediaType, setSelectedMediaType] = useState<VaultMediaType>(
    VaultMediaType.ALL,
  )

  const types = useMemo(() => getMediaTypes(allMedias), [allMedias])

  const filteredMedias = useMemo(
    () =>
      allMedias.filter(
        (media) =>
          selectedMediaType === VaultMediaType.ALL ||
          media.type === selectedMediaType,
      ),
    [allMedias, selectedMediaType],
  )

  const handleMediaPreview = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const mediaId = parseInt(event.currentTarget.dataset.mediaId!, 10)

      if (mediaId) {
        openDialog({
          type: DIALOG_TYPE.VAULT_MEDIA_PREVIEW,
          mediaList: allMedias.map(({ id }) => id),
          mediaId,
        })
      }
    },
    [allMedias],
  )

  return (
    <Dialog
      onClose={onClose}
      panelClassName="mt-0 sm:mt-0"
      bigDialog
      show={isOpen}
    >
      <Dialog.Header>
        <Dialog.Title>Attachments</Dialog.Title>
      </Dialog.Header>
      <div className="min-h-[50vh] py-6">
        <MediaTypeFilter
          onSelect={setSelectedMediaType}
          selected={selectedMediaType}
          types={types}
        />
        <div className="mt-4 grid  max-h-[50vh] grid-cols-3 gap-1 overflow-auto">
          {filteredMedias.map((media) => (
            <div
              className="relative col-span-1 aspect-square h-fit overflow-hidden rounded-md"
              key={`m-${media.id}`}
              data-media-id={media.id}
              onClick={handleMediaPreview}
            >
              {media.files && (
                <VaultMediaPreview
                  className="relative z-10 h-full"
                  fit="cover"
                  files={media.files}
                  type={media.type as VaultMediaType}
                />
              )}
              <Skeleton className="absolute inset-0 z-0" />
            </div>
          ))}
        </div>
      </div>
      <div className="w-full">
        <Button
          variant="outline"
          className="w-full"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    </Dialog>
  )
}
